import type { LiveImportantDate } from '@wiztime/core';
import type { CalendarApi } from './calendarModule';
import {
  markNotified,
  notificationsDue,
  readableDates,
  upcomingEntries,
  type DateEntry,
  type NotifiedState,
  type ReadableDate,
} from './model';

/** Everything the notification check needs from the outside, so it can run without a browser. */
export interface NotificationDeps {
  /** Every live important date. */
  readonly loadDates: () => Promise<readonly LiveImportantDate[]>;
  readonly loadCalendar: () => Promise<CalendarApi>;
  readonly readState: () => NotifiedState;
  readonly writeState: (state: NotifiedState) => void;
  /** Show one notification; false when it could not be shown (it is then tried again at the next check). */
  readonly show: (entry: DateEntry) => Promise<boolean>;
  /** Serialises checks across the tabs of a browser, so two tabs never announce the same date twice. */
  readonly withLock: <T>(body: () => Promise<T>) => Promise<T>;
}

const NO_HOLIDAYS = { holidayPacks: [], holidayWindowDays: 0 } as const;

/**
 * Announce the important dates for which `today` (a calendar date of the device) is a lead day
 * and that were not announced today yet; returns the ids announced. Without an active important
 * date the calendar module is not even loaded. A notification that could not be shown is not
 * recorded, so the next check tries it again.
 */
export async function checkImportantDateNotifications(
  deps: NotificationDeps,
  today: string,
): Promise<string[]> {
  const dates: ReadableDate[] = readableDates(await deps.loadDates(), true);
  if (dates.length === 0) return [];
  const calendar = await deps.loadCalendar();
  const entries = upcomingEntries(calendar, dates, NO_HOLIDAYS, today);
  return deps.withLock(async () => {
    // Read inside the lock: another tab may have announced in the meantime.
    const state = deps.readState();
    const due = notificationsDue(entries, state, today);
    const announced: string[] = [];
    for (const entry of due) {
      if (await deps.show(entry)) announced.push(entry.id);
    }
    if (announced.length > 0) deps.writeState(markNotified(state, announced, today));
    return announced;
  });
}

const NOTIFY_LOCK = 'wiztime-important-date';

/** Run `body` under a Web Lock; without the Web Locks API (older browsers) it runs directly. */
export function withNotifyLock<T>(body: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks === undefined ? body() : locks.request(NOTIFY_LOCK, body);
}
